import React from 'react';

import {
  Main,
  Heading,
  Paragraph,
  List,
  ListItem,
  Link,
  Accordion,
  ContentGroup,
} from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { StyledLink } from 'client/components/styledLink/StyledLink';
import { Helmet } from 'react-helmet';

import { LegalResponsibilitiesContent } from './LegalResponsibilitiesPage.config';
import * as routes from '../manifest';

function LegalResponsibilitiesPage() {
  const {
    metaContent,
    backLabel,
    pageTitle,
    asALandlord,
    yourRentalPropertyMust,
    tenantResponsibilities,
    tenantsInEngland,
    walesAccordion,
    scotlandAccordion,
    northernIrelandAccordion,
    newLandlordAccordion,
  } = useContent<LegalResponsibilitiesContent>();

  return (
    <Main>
      <Helmet>
        <title>{metaContent.title}</title>
      </Helmet>
      <ContentGroup marginTop="05" marginBottom="none">
        <StyledLink
          iconPosition="left"
          data-testid="back-button-link"
          to={routes.LandlordSupport}
        >
          {backLabel}
        </StyledLink>
      </ContentGroup>
      <Heading as="h1" marginTop="05" size="s7">
        {pageTitle}
      </Heading>
      <Paragraph>{asALandlord}</Paragraph>

      <Heading as="h2" size="s3">
        {yourRentalPropertyMust.title}
      </Heading>
      <List>
        <ListItem>{yourRentalPropertyMust.listItem1}</ListItem>
        <ListItem>{yourRentalPropertyMust.listItem2}</ListItem>
        <ListItem>{yourRentalPropertyMust.listItem3}</ListItem>
        <ListItem>
          {yourRentalPropertyMust.listItem4}
          <Link href={routes.Sustainability}>
            {yourRentalPropertyMust.linkLabel}
          </Link>
          {yourRentalPropertyMust.epc}
        </ListItem>
      </List>

      <Heading as="h2" size="s3">
        {tenantResponsibilities.title}
      </Heading>
      <List>
        <ListItem>{tenantResponsibilities.listItem1}</ListItem>
        <ListItem>{tenantResponsibilities.listItem2}</ListItem>
      </List>

      <Heading as="h2" size="s3">
        {tenantsInEngland.title}
      </Heading>
      <Paragraph>
        {tenantsInEngland.givenACopy}
        <Link
          data-testid="checklist-link"
          href={tenantsInEngland.checklistLinkHref}
          external
        >
          {tenantsInEngland.checklistLinkLabel}
        </Link>
        {tenantsInEngland.period}
      </Paragraph>
      <Paragraph>
        {tenantsInEngland.findLatestInfo}
        <Link
          data-testid="renters-rights-link"
          href={tenantsInEngland.rentersRightsLinkHref}
          external
        >
          {tenantsInEngland.rentersRightsLinkLabel}
        </Link>
        {tenantsInEngland.period}
      </Paragraph>
      <Paragraph>{tenantsInEngland.rentalFlats}</Paragraph>
      <Paragraph>
        <Link
          data-testid="purpose-built-flats-link"
          href={tenantsInEngland.purposeBuiltLinkHref}
          external
        >
          {tenantsInEngland.purposeBuiltLinkLabel}
        </Link>
      </Paragraph>
      <Paragraph>
        <Link
          data-testid="adapted-flats-link"
          href={tenantsInEngland.adaptedLinkHref}
          external
        >
          {tenantsInEngland.adaptedLinkLabel}
        </Link>
      </Paragraph>

      <Accordion
        data-testid="wales-accordion"
        label={walesAccordion.label}
        variation="standalone"
      >
        <Paragraph>
          {walesAccordion.landlordInWales}
          <Link
            data-testid="rent-smart-link"
            href={walesAccordion.rentSmartLinkHref}
            external
          >
            {walesAccordion.rentSmartLinkLabel}
          </Link>
          {walesAccordion.finedIfNotRegistered}
        </Paragraph>
        <Paragraph>{walesAccordion.rentingHomesAct}</Paragraph>
        <Paragraph>{walesAccordion.contractHolders}</Paragraph>
        <Paragraph>{walesAccordion.privateLandlords}</Paragraph>
        <List>
          {walesAccordion.listItems.map((item) => (
            <ListItem key={item.split(' ')[0]}>{item}</ListItem>
          ))}
        </List>
        <Paragraph>{walesAccordion.writtenStatement}</Paragraph>
        <Paragraph marginBottom="none">
          <Link
            data-testid="wales-landlord-responsibilities-link"
            href={walesAccordion.responsibilitiesLinkHref}
            external
          >
            {walesAccordion.responsibilitiesLinkLabel}
          </Link>
        </Paragraph>
      </Accordion>

      <Accordion
        data-testid="scotland-accordion"
        label={scotlandAccordion.label}
        variation="standalone"
        marginTop="05"
      >
        <Paragraph>
          {scotlandAccordion.additionalResponsibilities}
          <Link
            data-testid="legionella-link"
            href={scotlandAccordion.legionellaLinkHref}
            external
          >
            {scotlandAccordion.legionellaLinkLabel}
          </Link>
          {scotlandAccordion.and}
          <Link
            data-testid="soctland-landlord-registration-link"
            href={scotlandAccordion.localCouncilLinkHref}
            external
          >
            {scotlandAccordion.localCouncilLinkLabel}
          </Link>
          {scotlandAccordion.tenantsName}
        </Paragraph>
        <Paragraph marginBottom="none">
          <Link
            data-testid="scotland-landlord-responsibilities"
            href={scotlandAccordion.responsibilitiesLinkHref}
            external
          >
            {scotlandAccordion.responsibilitiesLinkLabel}
          </Link>
        </Paragraph>
      </Accordion>

      <Accordion
        data-testid="norther-ireland-accordion"
        label={northernIrelandAccordion.label}
        variation="standalone"
        marginTop="05"
      >
        <Paragraph>{northernIrelandAccordion.landlordRegister}</Paragraph>
        <Paragraph>
          <Link
            data-testid="norther-ireland-registration-link"
            href={northernIrelandAccordion.registrationProcessLinkHref}
            external
          >
            {northernIrelandAccordion.registrationProcessLinkLabel}
          </Link>
        </Paragraph>
        <Paragraph>
          {northernIrelandAccordion.tenancyInformationNotice}
        </Paragraph>
        <Paragraph marginBottom="none">
          <Link
            data-testid="norther-ireland-landlord-responsibilities"
            href={northernIrelandAccordion.responsibilitiesLinkHref}
            external
          >
            {northernIrelandAccordion.responsibilitiesLinkLabel}
          </Link>
        </Paragraph>
      </Accordion>

      <Accordion
        data-testid="new-landlord-accordion"
        label={newLandlordAccordion.label}
        variation="standalone"
        marginTop="05"
      >
        <Paragraph marginBottom="none">
          {newLandlordAccordion.findEverything}
          <Link
            data-testid="find-everything-here-link"
            href={newLandlordAccordion.findEverythingLinkHref}
            external
          >
            {newLandlordAccordion.linkLabel}
          </Link>
          {newLandlordAccordion.period}
        </Paragraph>
      </Accordion>
    </Main>
  );
}

export default LegalResponsibilitiesPage;
